import type { FoGesrPdfDocument } from './foGesrPdfDocument.ts';
import {
  addImageFormatFromDataUrl,
  fitInBox,
  naturalImageSize,
  rasterDataUrlNormalizedForPdf,
  resolveImageSrcForPdf,
} from './resolveImageForPdf.ts';

export type CellImageDraw = { data: string; fmt: 'PNG' | 'JPEG' | 'WEBP'; dw: number; dh: number };

/** Same shape as FO-GESR-2160 and FO-GESR-1775 worksheets. */
export type FoGesrTableDrawContext = {
  doc: FoGesrPdfDocument;
  y: { current: number };
  margin: number;
  pageH: number;
  w: number[];
  lh: number;
  pad: number;
  newPage: () => void;
  setNormal: (size: number) => void;
  setBold: (size: number) => void;
  cellLines: (text: string, colIdx: number, fontSize: number) => string[];
};

/** Row height for cells with text + photo + signature (FO-GESR-2160 / FO-GESR-1775). */
export function computeFoGesrParticipantImageRowHeight(params: {
  cellLines: FoGesrTableDrawContext['cellLines'];
  cells: string[];
  textColIndices: readonly number[];
  fs: number;
  lh: number;
  pad: number;
  fotoDraw: CellImageDraw | null;
  firmaDraw: CellImageDraw | null;
}): number {
  const { cellLines, cells, textColIndices, fs, lh, pad, fotoDraw, firmaDraw } = params;
  const linesNeeded = Math.max(
    ...textColIndices.map((i) => Math.max(cellLines(cells[i] ?? '', i, fs).length, 1)),
  );
  const textH = linesNeeded * lh + pad * 2;
  const fotoCellInner = fotoDraw ? fotoDraw.dh : lh;
  const firmaCellInner = firmaDraw ? firmaDraw.dh : lh;
  return Math.max(textH, fotoCellInner + pad * 2, firmaCellInner + pad * 2);
}
/** Ensure fo gesr table row fits page. */


export function ensureFoGesrTableRowFitsPage(ctx: FoGesrTableDrawContext, rowH: number): void {
  const { y, margin, pageH, newPage } = ctx;
  if (y.current + rowH > pageH - margin - 8) {
    newPage();
  }
}
/** Try resolve cell image draw. */


export async function tryResolveCellImageDraw(
  rawSrc: string,
  maxInnerW: number,
  maxInnerH: number,
): Promise<CellImageDraw | null> {
  const resolved = await resolveImageSrcForPdf(rawSrc);
  if (!resolved) return null;
  const normalized = await rasterDataUrlNormalizedForPdf(resolved);
  try {
    const { w: nw, h: nh } = await naturalImageSize(normalized);
    const { dw, dh } = fitInBox(nw, nh, maxInnerW, maxInnerH);
    return { data: normalized, fmt: addImageFormatFromDataUrl(normalized), dw, dh };
  } catch {
    return null;
  }
}
/** Draw fo gesr table text row. */


export function drawFoGesrTableTextRow(ctx: FoGesrTableDrawContext, cells: string[], header: boolean): void {
  const { doc, y, margin, pageH, w, lh, pad, newPage, setNormal, setBold, cellLines } = ctx;
  const fs = header ? 6.2 : 6;
  if (header) setBold(fs);
  else setNormal(fs);
  const lineCounts = cells.map((c, i) => Math.max(cellLines(c, i, fs).length, 1));
  const linesNeeded = Math.max(...lineCounts);
  const rowH = linesNeeded * lh + pad * 2;
  if (y.current + rowH > pageH - margin - 8) {
    newPage();
  }
  let x = margin;
  if (header) setBold(fs);
  else setNormal(fs);
  for (let i = 0; i < cells.length; i += 1) {
    const wi = w[i] ?? 0;
    doc.rect(x, y.current, wi, rowH);
    const lines = cellLines(cells[i] ?? '', i, fs);
    let yy = y.current + lh + pad * 0.35;
    for (const ln of lines) {
      doc.text(ln, x + pad, yy, { maxWidth: wi - pad * 2 });
      yy += lh;
    }
    x += wi;
  }
  y.current += rowH;
  setNormal(6);
}
/** Draw fo gesr table image cell. */


export async function drawFoGesrTableImageCell(
  doc: FoGesrPdfDocument,
  x: number,
  yTop: number,
  wi: number,
  pad: number,
  lh: number,
  draw: CellImageDraw | null,
): Promise<void> {
  if (draw) {
    const ix = x + pad + (wi - pad * 2 - draw.dw) / 2;
    const iy = yTop + pad;
    await doc.addImage(draw.data, draw.fmt, ix, iy, draw.dw, draw.dh);
  } else {
    doc.text('—', x + pad, yTop + lh + pad * 0.35);
  }
}

export type FoGesrTextColumnLayout = {
  colIdx: number;
  x: number;
  yTop: number;
  rowH: number;
  wi: number;
  fs: number;
};
/** Draw fo gesr text column. */


export function drawFoGesrTextColumn(
  ctx: FoGesrTableDrawContext,
  cells: string[],
  layout: FoGesrTextColumnLayout,
): number {
  const { colIdx, x, yTop, rowH, wi, fs } = layout;
  const { doc, lh, pad, cellLines } = ctx;
  doc.rect(x, yTop, wi, rowH);
  const lines = cellLines(cells[colIdx] ?? '', colIdx, fs);
  let yy = yTop + lh + pad * 0.35;
  for (const ln of lines) {
    doc.text(ln, x + pad, yy, { maxWidth: wi - pad * 2 });
    yy += lh;
  }
  return x + wi;
}
